import React, { useState, useEffect, useContext } from "react"

import {
  ChevronDownIcon,
  ClockIcon,
  EyeIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  PencilIcon,
  SparklesIcon,
  StarIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid"
import classNames from "classnames"
import * as DateFns from "date-fns"
import useOutsideClick from "../../../../hooks/useOutsideClick"
import { FleetingIdea, ID } from "../../../../../model/types"
import type { IdeaColourMode } from "../../../../../plate_viewer/idea_colour"
import { useNana } from "../../../../hooks/useNana"
import { plainTextFromHTML } from "../../../../../services/text"
import { MosaicPageState } from "../../mosaic"
import { DeviceContext } from "../../../../providers/device_context"

// Mobile keeps a large tap target (h-12); desktop is ~25% smaller so the bar
// sits below navbar weight.
const toolbarButtonClass = (
  active: boolean,
  { withBorder = true }: { withBorder?: boolean } = {},
) =>
  classNames(
    "flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500 md:h-9 md:w-9",
    {
      "border-r border-gray-300 dark:border-gray-700": withBorder,
      "text-gray-600 hover:bg-sky-50 dark:text-gray-200 dark:hover:bg-gray-700": !active,
      "bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-100": active,
    },
  )

const toolbarIconClass = "h-5 w-5 md:h-4 md:w-4"

type HeatmapColourMode = "creationTime" | "editTime" | "lastViewedTime"

// Parent idle icon is Clock (time heatmaps). Creation uses sparkles ("new")
// so it stays distinct from the dropdown button.
const COLOUR_MODE_OPTIONS: {
  mode: HeatmapColourMode
  label: string
  description: string
  icon: typeof ClockIcon
}[] = [
  {
    mode: "creationTime",
    label: "Creation time",
    description: "Colour ideas by when they were created",
    icon: SparklesIcon,
  },
  {
    mode: "editTime",
    label: "Last edit",
    description: "Colour ideas by when they were last edited",
    icon: PencilIcon,
  },
  {
    mode: "lastViewedTime",
    label: "Last viewed",
    description: "Colour ideas by when they were last viewed",
    icon: EyeIcon,
  },
]

const FleetingIdeaPanel = ({
  setDraggingFleetingIdea,
  colourMode,
  setColourMode,
  locked,
  setLocked,
}: {
  setDraggingFleetingIdea: (i: FleetingIdea) => void
  colourMode: IdeaColourMode
  setColourMode: (mode: IdeaColourMode) => void
  locked: boolean
  setLocked: (locked: boolean) => void
}) => {
  const { model, hub } = useNana()
  const { platform } = useContext(DeviceContext)
  const [isOpen, setIsOpen] = useState(false)
  const [colourMenuOpen, setColourMenuOpen] = useState(false)
  const [ideas, setIdeas] = useState([] as FleetingIdea[])
  const [highlightId, setHighlightId] = useState<ID | null>(null)

  const openIdeaPicker = () => {
    window.dispatchEvent(new CustomEvent("open-fuzzy-finder", {}))
  }
  const ideaPickerShortcut = platform === "mac" ? "⌘K" : "Ctrl+K"

  const setup = () => {
    setIdeas(model.getFleetingIdeas())
  }

  useEffect(() => {
    setup()
    const keys = [
      hub.on("model:fleeting-idea-created", setup),
      hub.on("model:fleeting-idea-deleted", setup),
    ]
    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [])

  // Deep-link from Now (and elsewhere): open panel and highlight a fleeting note
  useEffect(() => {
    if (MosaicPageState.openFleetingPanel) {
      setIsOpen(true)
      MosaicPageState.openFleetingPanel = false
    }
    if (MosaicPageState.highlightFleetingId != null) {
      setHighlightId(MosaicPageState.highlightFleetingId)
      MosaicPageState.highlightFleetingId = null
    }
  }, [])

  const fleetingPanelRef = useOutsideClick(() => {
    const wasOpen = isOpen
    setIsOpen(false)
    return wasOpen
  }, isOpen)

  const colourMenuRef = useOutsideClick(() => {
    setColourMenuOpen(false)
    return true
  }, colourMenuOpen)

  const activeColourOption = COLOUR_MODE_OPTIONS.find((option) => option.mode === colourMode)
  const heatmapActive = activeColourOption != null
  const selfHighlightActive = colourMode === "self"
  const ColourModeIcon = activeColourOption?.icon ?? ClockIcon
  const colourModeLabel = activeColourOption
    ? `Time heatmap: ${activeColourOption.label}`
    : "Time heatmap"

  const selectColourMode = (mode: IdeaColourMode) => {
    setColourMode(mode)
    setColourMenuOpen(false)
  }

  const selectHeatmapNone = () => {
    // Only clear when a heatmap is on; leave self-highlight alone.
    if (heatmapActive) setColourMode("default")
    setColourMenuOpen(false)
  }

  const toggleSelfHighlight = () => {
    setColourMode(selfHighlightActive ? "default" : "self")
    setColourMenuOpen(false)
  }

  const colourMenuItemClass = (isActive: boolean) =>
    classNames(
      "flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500 md:gap-2 md:px-2.5 md:py-1.5 md:text-xs",
      {
        "bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-100": isActive,
        "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700": !isActive,
      },
    )

  const colourMenuIconClass = "h-5 w-5 shrink-0 md:h-4 md:w-4"

  return (
    <div className="pointer-events-none absolute left-5 right-5 top-[5.25rem] flex select-none flex-col items-start gap-2">
      <div className="relative" ref={colourMenuRef}>
        <div className="pointer-events-auto flex max-w-full items-stretch overflow-hidden rounded-lg border border-gray-300 bg-white shadow dark:border-gray-700 dark:bg-gray-800">
          <button
            type="button"
            aria-label={`Find idea (${ideaPickerShortcut})`}
            title={`Find idea (${ideaPickerShortcut})`}
            data-testid="mosaic-idea-picker-button"
            onClick={openIdeaPicker}
            className={toolbarButtonClass(false)}
          >
            <MagnifyingGlassIcon className={toolbarIconClass} />
          </button>
          <button
            type="button"
            aria-label={colourModeLabel}
            title={colourModeLabel}
            aria-haspopup="menu"
            aria-expanded={colourMenuOpen}
            aria-pressed={heatmapActive}
            data-testid="mosaic-colour-mode-button"
            onClick={() => setColourMenuOpen(!colourMenuOpen)}
            className={classNames(toolbarButtonClass(heatmapActive), "relative")}
          >
            <ColourModeIcon className={toolbarIconClass} />
            <ChevronDownIcon
              className={classNames(
                "absolute bottom-1.5 right-1.5 h-2.5 w-2.5 transition-transform md:bottom-1 md:right-1 md:h-2 md:w-2",
                { "rotate-180": colourMenuOpen },
              )}
            />
          </button>
          <button
            type="button"
            aria-label="Highlight ideas from myself"
            title="Highlight ideas from myself"
            aria-pressed={selfHighlightActive}
            data-testid="mosaic-self-highlight-button"
            onClick={toggleSelfHighlight}
            className={toolbarButtonClass(selfHighlightActive)}
          >
            <StarIcon className={toolbarIconClass} />
          </button>
          <button
            type="button"
            aria-label={locked ? "Unlock mosaic" : "Lock mosaic"}
            title={locked ? "Unlock mosaic (U)" : "Lock mosaic (U)"}
            aria-pressed={locked}
            data-testid="mosaic-lock-button"
            onClick={() => setLocked(!locked)}
            className={toolbarButtonClass(locked, { withBorder: ideas.length > 0 })}
          >
            {locked ? (
              <LockClosedIcon className={toolbarIconClass} />
            ) : (
              <LockOpenIcon className={toolbarIconClass} />
            )}
          </button>
          {ideas.length > 0 && (
            <button
              type="button"
              id="fleeting-cta"
              onClick={() => setIsOpen(!isOpen)}
              className="flex h-12 min-w-0 cursor-pointer items-center gap-4 px-4 text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500 dark:text-gray-100 dark:hover:bg-gray-700 md:h-9 md:gap-3 md:px-3 md:text-xs"
            >
              <div className="truncate whitespace-nowrap">
                <span>{ideas.length}</span> quick ideas to place
              </div>
              <ChevronDownIcon className="inline-block h-4 w-4 shrink-0 md:h-3.5 md:w-3.5" />
            </button>
          )}
        </div>
        {colourMenuOpen && (
          <div
            className="pointer-events-auto absolute left-0 top-full z-20 mt-1 w-56 origin-top-left overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:shadow-none md:w-48 md:rounded-md md:py-0.5"
            role="menu"
            aria-label="Time heatmap modes"
          >
            <button
              type="button"
              role="menuitemradio"
              aria-checked={!heatmapActive}
              title="Use each idea's own colour"
              data-testid="mosaic-colour-mode-none"
              onClick={selectHeatmapNone}
              className={colourMenuItemClass(!heatmapActive)}
            >
              <NoSymbolIcon className={colourMenuIconClass} />
              <span className="font-medium">None</span>
            </button>
            <div
              className="my-1 border-t border-gray-200 dark:border-gray-700 md:my-0.5"
              role="separator"
            />
            {COLOUR_MODE_OPTIONS.map((option) => {
              const Icon = option.icon
              const isActive = colourMode === option.mode
              return (
                <button
                  key={option.mode}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  title={option.description}
                  onClick={() => selectColourMode(option.mode)}
                  className={colourMenuItemClass(isActive)}
                >
                  <Icon className={colourMenuIconClass} />
                  <span className="font-medium">{option.label}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
      {ideas.length > 0 && isOpen && (
        <div
          className="pointer-events-auto z-10 flex h-96 w-full max-w-lg origin-top-right flex-col justify-between rounded-md bg-white shadow ring-1 ring-black ring-opacity-5 focus:outline-none dark:border dark:border-gray-700 dark:bg-gray-800 dark:shadow-none md:h-72 md:max-w-md"
          ref={fleetingPanelRef}
        >
          <div className="flex w-full flex-col items-start overflow-y-scroll py-4 md:py-2">
            {[...ideas]
              .sort((a, b) => {
                if (highlightId != null) {
                  if (a.id === highlightId) return -1
                  if (b.id === highlightId) return 1
                }
                return DateFns.compareDesc(a.createdAt, b.createdAt)
              })
              .map((idea) => (
                <div
                  key={idea.id}
                  className={classNames(
                    "flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 md:gap-1.5 md:px-3 md:py-1.5",
                    {
                      "bg-sky-50 dark:bg-sky-950 ring-1 ring-inset ring-sky-200 dark:ring-sky-800":
                        highlightId != null && idea.id === highlightId,
                    },
                  )}
                >
                  <div
                    className="box-border flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[3px] border bg-gray-100 text-gray-400 md:h-6 md:w-6"
                    style={{ borderColor: "rgba(0, 0, 0, 0.16)" }}
                    draggable="true"
                    onDragStart={() => setDraggingFleetingIdea(idea)}
                  >
                    <XMarkIcon className="h-5 w-5 md:h-3.5 md:w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1 truncate text-sm sm:w-80 md:text-xs">
                    {plainTextFromHTML(idea.content)}
                  </div>
                </div>
              ))}
          </div>

          <div className="border-t bg-gray-100 p-2 px-4 text-xs text-gray-500 dark:border-zinc-700 dark:bg-gray-900 md:px-3 md:py-1.5 md:text-[11px]">
            Drag the cell to place in the mosaic
          </div>
        </div>
      )}
    </div>
  )
}

export default FleetingIdeaPanel
